import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Divider,
    Form,
    Input,
    List,
    message,
    Space,
    Typography,
} from "antd";
import TotpAPI from "../api/totp";

const { Paragraph, Text, Title } = Typography;

export default function Security() {
    const [messageApi, contextHolder] = message.useMessage();
    const [status, setStatus] = useState(null);
    const [enrollment, setEnrollment] = useState(null);
    const [backupCodes, setBackupCodes] = useState(null);
    const [disableUseBackup, setDisableUseBackup] = useState(false);
    const [disableForm] = Form.useForm();

    const loadStatus = () => {
        TotpAPI.status(
            (res) => setStatus(res),
            (err) => console.log(err),
        );
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const startEnroll = () => {
        TotpAPI.enroll(
            (res) => setEnrollment(res),
            (err) => messageApi.error("Failed to start enrollment"),
        );
    };

    const onActivate = (values) => {
        TotpAPI.activate(
            { totp_id: enrollment.totp_id, code: values.code },
            (res) => {
                setBackupCodes(res.backup_codes);
                setEnrollment(null);
                loadStatus();
            },
            (err) => messageApi.error("Invalid code"),
        );
    };

    const onDisable = (values) => {
        TotpAPI.disable(
            { code: values.code },
            (res) => {
                messageApi.success("2FA disabled");
                setBackupCodes(null);
                loadStatus();
            },
            (err) => messageApi.error("Invalid code"),
        );
    };

    if (backupCodes) {
        return <Card title="Save Your Backup Codes">
            {contextHolder}
            <Alert
                type="warning"
                showIcon
                message="These codes are shown only once."
                description="Save them somewhere safe. Each code works once if you lose access to your authenticator app."
                style={{ marginBottom: 16 }}
            />
            <List
                bordered
                dataSource={backupCodes}
                renderItem={(code) => (
                    <List.Item><Text code>{code}</Text></List.Item>
                )}
            />
            <Divider />
            <Button type="primary" onClick={() => setBackupCodes(null)}>I have saved them</Button>
        </Card>;
    }

    if (enrollment) {
        return <Card title="Enroll Authenticator">
            {contextHolder}
            <Paragraph>
                Scan this QR code with any authenticator app (Google Authenticator,
                Microsoft Authenticator, Authy, 1Password, Apple Passwords, etc.).
            </Paragraph>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img
                    alt="TOTP QR"
                    src={`data:image/png;base64,${enrollment.qr_png_b64}`}
                    style={{ maxWidth: 240 }}
                />
            </div>
            <Paragraph>
                Or enter this secret manually:
                <br />
                <Text code copyable>{enrollment.secret}</Text>
            </Paragraph>
            <Divider />
            <Paragraph>Enter the 6-digit code from the app to confirm:</Paragraph>
            <Form onFinish={onActivate} layout="inline">
                <Form.Item name="code" rules={[{ required: true }]}>
                    <Input.OTP
                        length={6}
                        autoFocus
                        formatter={(s) => s.replace(/\D/g, '')}
                    />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">Activate</Button>
                        <Button onClick={() => setEnrollment(null)}>Cancel</Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>;
    }

    return <Card title="Security">
        {contextHolder}
        <Title level={5}>Two-Factor Authentication</Title>
        {status === null && <Paragraph>Loading...</Paragraph>}
        {status && !status.enrolled && <>
            <Paragraph>
                2FA is <Text strong>not enabled</Text>. Enable it to add an extra
                layer of protection when signing in.
            </Paragraph>
            <Button type="primary" onClick={startEnroll}>Enable 2FA</Button>
        </>}
        {status && status.enrolled && <>
            <Alert
                type="success"
                showIcon
                message="2FA is enabled"
                description={`You have ${status.backup_codes_remaining} backup code${status.backup_codes_remaining === 1 ? '' : 's'} remaining.`}
                style={{ marginBottom: 16 }}
            />
            <Paragraph>
                To disable 2FA, enter a current 6-digit code or a backup code:
            </Paragraph>
            <Form form={disableForm} onFinish={onDisable} layout="inline">
                <Form.Item name="code" rules={[{ required: true }]}>
                    {disableUseBackup ? (
                        <Input placeholder="Backup code" />
                    ) : (
                        <Input.OTP
                            length={6}
                            formatter={(s) => s.replace(/\D/g, '')}
                        />
                    )}
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button danger htmlType="submit">Disable 2FA</Button>
                        <Button
                            type="link"
                            onClick={() => {
                                setDisableUseBackup((v) => !v);
                                disableForm.resetFields(['code']);
                            }}
                        >
                            {disableUseBackup
                                ? 'Use authenticator code'
                                : 'Use a backup code instead'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </>}
    </Card>;
}
